import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';

import { NotificationService } from '@/app/core/services/common/notification.service';
import { Store } from '@ngrx/store';
import { I18nService } from '@/app/core/services/mock';
import { ApiError, ApiRequest, ApiResponse, JsonHeader } from '@/app/core/models';
import { EnvService } from '@/app/core/services/common/env.service';

export interface ApiCallbacks<T = unknown> {
  onSuccess?: (data: T) => void;
  onError?: (error: ApiError) => void;
}

/**
 * Base API Service
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  protected readonly envService = inject(EnvService);
  protected readonly http = inject(HttpClient);
  protected readonly i18n = inject(I18nService);
  protected readonly notification = inject(NotificationService);
  protected readonly store = inject(Store);

  /**
   * Get business API URL - computed dynamically từ env config
   * Tính toán mỗi lần gọi để đảm bảo lấy config mới nhất
   */
  private getBusinessApiUrl(): string {
    const apiUrl = this.envService.getApiUrl();
    return `${apiUrl}/api/business/public`;
  }

  /**
   * Execute business API POST request
   */
  public executePostRequest<TResponse = any, TRequest = any>(
    bizType: string,
    jsonParam: TRequest = {} as TRequest,
    headerOverrides?: Partial<JsonHeader>,
    onSuccess?: (data: TResponse) => void,
    onError?: (error: ApiError) => void,
  ): Observable<ApiResponse<TResponse>> {
    return this.executeApiCall<TRequest, TResponse>(
      'POST',
      bizType,
      jsonParam,
      headerOverrides,
      onSuccess,
      onError,
    );
  }

  /**
   * Core API execution method
   */
  private executeApiCall<TRequest, TResponse>(
    method: 'POST' | 'GET' | 'DELETE',
    bizType: string,
    jsonParam: TRequest,
    headerOverrides?: Partial<JsonHeader>,
    onSuccess?: (data: TResponse) => void,
    onError?: (error: ApiError) => void,
  ): Observable<ApiResponse<TResponse>> {
    // Lấy URL mỗi lần gọi API để đảm bảo config mới nhất
    const businessApiUrl = this.getBusinessApiUrl();
    
    const request: ApiRequest<TRequest> = {
      bizType,
      JsonHeader: this.createHeader(headerOverrides),
      jsonParam,
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    
    });

    return this.http
      .post<ApiResponse<TResponse>>(businessApiUrl, request, {
        headers,
      })
      .pipe(
        map((response) => this.handleResponse(response, onSuccess, onError)),
        catchError((error) => this.handleError(error, onError)),
      );
  }

  private createHeader(overrides?: Partial<JsonHeader>): JsonHeader {
    return {
      lang: 'VI',
      user: '',
      channel: 'BACK',
      branchCode: '',
      branchId: 0,
      ...(overrides || {}),
    };
  }

  private handleResponse<T>(
    response: ApiResponse<T>,
    onSuccess?: (data: T) => void,
    onError?: (error: ApiError) => void,
  ): ApiResponse<T> {
    if (response.Rc && response.Rc !== 0) {
      const error: ApiError = {
        code: response.Rs || `RS_${response.Rs}`,
        message: response.RsMsg || this.i18n.translate('errors.general_failure'),
      };
      if (onError) {
        onError(error);
      } else {
        this.notification.error(error.message);
      }
      throw error;
    }
    if (onSuccess) {
      onSuccess(response.Data);
    }

    return response;
  }

  public handleError<T>(
    error: HttpErrorResponse,
    onError?: (error: ApiError) => void,
  ): Observable<never> {
    const apiError: ApiError =
      error.error instanceof ErrorEvent
        ? {
            code: 'CLIENT_ERROR',
            message: error.error.message,
            details: this.i18n.translate('errors.client_error'),
          }
        : {
            code: `HTTP_${error.status}`,
            message: error.message || this.i18n.translate('errors.server_error'),
            status: error.status,
          };
    onError?.(apiError);
    return throwError(() => apiError);
  }

  public executePostRequest2<TResponse>(
    apiUrl: string,
    onSuccess?: (data: TResponse) => void,
    onError?: (error: ApiError) => void,
  ): Observable<TResponse> {

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });

    return this.http
      .get<TResponse>(apiUrl, { headers }) 
      .pipe(
        map((response) => {
          onSuccess?.(response);
          return response; 
        }),
        catchError((error) => this.handleError(error, onError)),
      );
  }

  /**
   * Execute POST request to HIS API (API_PushThongTin)
   */
  public executePostRequestHIS<TResponse, TRequest = any>(
    apiUrl: string,
    body: TRequest,
    onSuccess?: (data: TResponse) => void,
    onError?: (error: ApiError) => void,
  ): Observable<TResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http
      .post<TResponse>(apiUrl, body, { headers })
      .pipe(
        map((response) => {
          onSuccess?.(response);
          return response;
        }),
        catchError((error) => this.handleError(error, onError)),
      );
  }
}
